import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, MessageCircle, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { api, todayISO, typeBadge, fmt, idDate } from "../lib/dimsum";

const inputCls =
  "w-full rounded-2xl border-2 border-stone-300 bg-stone-50 px-4 py-3.5 text-base sm:text-lg font-semibold text-stone-900 focus:border-rose-500 focus:bg-white focus:outline-none transition-colors";
const labelCls = "text-sm sm:text-base font-bold text-stone-800 mb-1.5 block";

const Stepper = ({ value, onChange, testid }) => (
  <input
    data-testid={testid}
    type="number"
    inputMode="decimal"
    placeholder="0"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={inputCls + " text-center font-mono-num"}
  />
);

const PersonAutocomplete = ({ label, value, onChange, options, exclude, testid, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ex = (exclude || "").trim().toLowerCase();
  const q = (value || "").trim().toLowerCase();
  const matches = options.filter(
    (o) =>
      o.toLowerCase().includes(q) &&
      o !== value &&
      o.toLowerCase() !== ex
  );
  const canShow = q.length >= 1;
  return (
    <div className="relative">
      <label className={labelCls}>{label}</label>
      <input
        data-testid={testid}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputCls}
      />
      {open && canShow && matches.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-1.5 w-full max-h-52 overflow-auto rounded-2xl border-2 border-stone-200 bg-white shadow-lg"
        >
          {matches.map((o) => (
            <li key={o}>
              <button
                type="button"
                data-testid={`${testid}-option-${o}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(o);
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 font-semibold text-stone-800 hover:bg-emerald-50 transition-colors"
              >
                {o}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <p className="text-xs font-bold uppercase tracking-wider text-stone-400 pt-0.5">
      {label}
    </p>
    <p className="text-sm font-bold text-stone-800 text-right">{value}</p>
  </div>
);

const ConfirmModal = ({
  date,
  members,
  doughSel,
  doughKg,
  selected,
  pax,
  notes,
  totalDough,
  totalPax,
  saving,
  onEdit,
  onConfirm,
}) => (
  <div
    className="no-print fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    data-testid="recap-confirm-modal"
  >
    <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
      <div className="flex items-center gap-3 bg-amber-50 border-b-2 border-amber-200 px-6 py-4">
        <div className="grid place-items-center w-11 h-11 rounded-2xl bg-amber-500 text-white">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <div>
          <p className="font-display font-extrabold text-lg text-stone-900">
            Mohon periksa lagi data Anda
          </p>
          <p className="text-sm font-semibold text-stone-500">
            Pastikan semua sudah benar sebelum dikirim
          </p>
        </div>
      </div>

      <div className="px-6 py-4 space-y-3 max-h-[50vh] overflow-auto">
        <Row label="Tanggal" value={idDate(date)} />
        <Row label="Peserta" value={members.join(" & ") || "-"} />
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-1">
            Adonan
          </p>
          <div className="flex flex-wrap gap-1.5">
            {doughSel.map((c) => (
              <span
                key={c}
                className={`rounded-lg border px-2 py-0.5 text-xs font-bold ${
                  typeBadge(c)
                }`}
              >
                {c} {fmt(doughKg[c])}kg
              </span>
            ))}
          </div>
          <p className="mt-1 text-sm font-bold text-orange-700">
            Total {fmt(totalDough)} kg
          </p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-1">
            Dimsum
          </p>
          <div className="flex flex-wrap gap-1.5">
            {selected.map((c) => (
              <span
                key={c}
                className={`rounded-lg border px-2 py-0.5 text-xs font-bold ${
                  typeBadge(c)
                }`}
              >
                {c} {fmt(pax[c])}pax
              </span>
            ))}
          </div>
          <p className="mt-1 text-sm font-bold text-emerald-700">
            Total {fmt(totalPax)} pax
          </p>
        </div>
        {notes.trim() && <Row label="Catatan" value={notes.trim()} />}
      </div>

      <div className="flex gap-3 px-6 py-4 border-t border-stone-100">
        <button
          data-testid="confirm-edit-button"
          onClick={onEdit}
          disabled={saving}
          className="flex-1 min-h-[48px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Pencil className="w-4 h-4" /> Edit
        </button>
        <button
          data-testid="confirm-save-send-button"
          onClick={onConfirm}
          disabled={saving}
          className="flex-1 min-h-[48px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <MessageCircle className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan & Kirim"}
        </button>
      </div>
    </div>
  </div>
);


const TypeAutocomplete = ({ options, selected, onPick, testid, placeholder }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const matches = options.filter(
    (o) => !selected.includes(o.code) && o.code.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div className="relative mb-4">
      <input
        data-testid={testid}
        type="text"
        value={q}
        placeholder={placeholder}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputCls}
      />
      {open && matches.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-1.5 w-full max-h-56 overflow-auto rounded-2xl border-2 border-stone-200 bg-white shadow-lg"
        >
          {matches.map((o) => (
            <li key={o.code}>
              <button
                type="button"
                data-testid={`${testid}-option-${o.code}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(o.code);
                  setQ("");
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 font-bold text-stone-800 hover:bg-amber-50 transition-colors"
              >
                {o.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RecapSummary = ({ title, dough_items, items, notes, highlight }) => {
  const tDough = dough_items.reduce((s, d) => s + Number(d.kg || 0), 0);
  const tPax = items.reduce((s, i) => s + Number(i.pax || 0), 0);
  return (
    <div
      className={`rounded-2xl border-2 p-3 ${
        highlight ? "border-emerald-300 bg-emerald-50/60" : "border-stone-200 bg-stone-50"
      }`}
    >
      <p
        className={`text-xs font-extrabold uppercase tracking-wider mb-2 ${
          highlight ? "text-emerald-700" : "text-stone-500"
        }`}
      >
        {title}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {dough_items.map((d) => (
          <span key={d.dough_type} className={`rounded-lg border px-2 py-0.5 text-xs font-bold ${typeBadge(d.dough_type)}`}>
            {d.dough_type} {fmt(d.kg)}kg
          </span>
        ))}
      </div>
      <p className="text-xs font-bold text-orange-700 mb-2">Total {fmt(tDough)} kg</p>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {items.map((i) => (
          <span key={i.dimsum_type} className={`rounded-lg border px-2 py-0.5 text-xs font-bold ${typeBadge(i.dimsum_type)}`}>
            {i.dimsum_type} {fmt(i.pax)}pax
          </span>
        ))}
      </div>
      <p className="text-xs font-bold text-emerald-700">Total {fmt(tPax)} pax</p>
      {notes && notes.trim() && (
        <p className="mt-2 text-xs font-semibold text-stone-600">Catatan: {notes.trim()}</p>
      )}
    </div>
  );
};

const DuplicateModal = ({ date, members, oldRecap, newData, saving, onCancel, onConfirm }) => (
  <div
    className="no-print fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    data-testid="recap-duplicate-modal"
  >
    <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden">
      <div className="flex items-center gap-3 bg-amber-50 border-b-2 border-amber-200 px-6 py-4">
        <div className="grid place-items-center w-11 h-11 rounded-2xl bg-amber-500 text-white">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <div>
          <p className="font-display font-extrabold text-lg text-stone-900">
            Data hari ini sudah ada
          </p>
          <p className="text-sm font-semibold text-stone-500">
            Apakah Anda ingin mengganti data pada hari ini?
          </p>
        </div>
      </div>

      <div className="px-6 py-4 space-y-3 max-h-[55vh] overflow-auto">
        <Row label="Tanggal" value={idDate(date)} />
        <Row label="Peserta" value={members.join(" & ") || "-"} />
        <RecapSummary
          title="Data Sebelumnya"
          dough_items={oldRecap.dough_items || []}
          items={oldRecap.items || []}
          notes={oldRecap.notes}
        />
        <RecapSummary
          title="Data Baru"
          dough_items={newData.dough_items}
          items={newData.items}
          notes={newData.notes}
          highlight
        />
      </div>

      <div className="flex gap-3 px-6 py-4 border-t border-stone-100">
        <button
          data-testid="dup-cancel-button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 min-h-[48px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold disabled:opacity-60"
        >
          Batal
        </button>
        <button
          data-testid="dup-confirm-button"
          onClick={onConfirm}
          disabled={saving}
          className="flex-1 min-h-[48px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <MessageCircle className="w-4 h-4" /> {saving ? "Menyimpan…" : "Ya, Simpan & Bagikan ke WA"}
        </button>
      </div>
    </div>
  </div>
);

const ConflictModal = ({ date, conflicts, onClose }) => (
  <div
    className="no-print fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    data-testid="recap-conflict-modal"
  >
    <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
      <div className="flex items-center gap-3 bg-rose-50 border-b-2 border-rose-200 px-6 py-4">
        <div className="grid place-items-center w-11 h-11 rounded-2xl bg-rose-600 text-white">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <div>
          <p className="font-display font-extrabold text-lg text-stone-900">
            Peserta sudah dipakai hari ini
          </p>
          <p className="text-sm font-semibold text-stone-500">
            Satu peserta hanya bisa dipilih sekali dalam sehari
          </p>
        </div>
      </div>

      <div className="px-6 py-4 space-y-3">
        <Row label="Tanggal" value={idDate(date)} />
        <div className="space-y-2">
          {conflicts.map((c) => (
            <p
              key={c.member}
              data-testid={`conflict-${c.member}`}
              className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm font-semibold text-stone-800"
            >
              <span className="font-extrabold text-rose-700">{c.member}</span> sudah bersama{" "}
              <span className="font-extrabold text-rose-700">
                {c.partners.join(" & ") || "peserta lain"}
              </span>{" "}
              hari ini.
            </p>
          ))}
        </div>
        <p className="text-sm font-semibold text-stone-500">
          Peserta tersebut baru dapat dipilih lagi besok setelah pukul 00.00 WIB.
        </p>
      </div>

      <div className="px-6 py-4 border-t border-stone-100">
        <button
          data-testid="conflict-close-button"
          onClick={onClose}
          className="w-full min-h-[48px] rounded-2xl bg-stone-800 hover:bg-stone-900 text-white font-bold"
        >
          Mengerti
        </button>
      </div>
    </div>
  </div>
);

export const RecapForm = ({ teams, dimsumTypes, doughTypes, onSaved }) => {
  const [date, setDate] = useState(todayISO());
  const [person1, setPerson1] = useState("");
  const [person2, setPerson2] = useState("");
  const [doughKg, setDoughKg] = useState({});
  const [doughSel, setDoughSel] = useState([]);
  const [pax, setPax] = useState({});
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupExisting, setDupExisting] = useState(null);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const totalPax = useMemo(
    () => Object.values(pax).reduce((s, v) => s + (Number(v) || 0), 0),
    [pax]
  );
  const totalDough = useMemo(
    () => Object.values(doughKg).reduce((s, v) => s + (Number(v) || 0), 0),
    [doughKg]
  );
  const ratio = totalDough > 0 ? (totalPax / totalDough).toFixed(1) : 0;

  const memberOptions = useMemo(
    () => Array.from(new Set(teams.flatMap((t) => t.members))).sort(),
    [teams]
  );

  const [summaryPeriod, setSummaryPeriod] = useState("day");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [summary, setSummary] = useState({ dough: 0, pax: 0, count: 0, types: [] });

  useEffect(() => {
    const now = new Date();
    const iso = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    };
    let params = "";
    if (summaryPeriod === "day") {
      params = `?start=${iso(now)}&end=${iso(now)}`;
    } else if (summaryPeriod === "week") {
      const dow = (now.getDay() + 6) % 7;
      const mon = new Date(now);
      mon.setDate(now.getDate() - dow);
      const e = new Date(mon);
      e.setDate(mon.getDate() + 4);
      const s = new Date(e);
      s.setDate(e.getDate() - 7);
      params = `?start=${iso(s)}&end=${iso(e)}`;
    } else if (summaryPeriod === "month") {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      params = `?start=${iso(s)}&end=${iso(e)}`;
    } else if (summaryPeriod === "custom") {
      const qp = [];
      if (rangeStart) qp.push(`start=${rangeStart}`);
      if (rangeEnd) qp.push(`end=${rangeEnd}`);
      params = qp.length ? `?${qp.join("&")}` : "";
    }
    Promise.all([api.get(`/recaps${params}`), api.get(`/lembur${params}`).catch(() => ({ data: [] }))])
      .then(([r, lr]) => {
        const data = r.data || [];
        const lemburs = lr.data || [];
        let dough = 0;
        let pax = 0;
        const byType = {};
        data.forEach((rr) => {
          (rr.dough_items || []).forEach((d) => (dough += Number(d.kg || 0)));
          (rr.items || []).forEach((i) => {
            pax += Number(i.pax || 0);
            byType[i.dimsum_type] = (byType[i.dimsum_type] || 0) + Number(i.pax || 0);
          });
        });
        const lemburByType = {};
        let lemburKg = 0;
        let lemburPax = 0;
        lemburs.forEach((l) => {
          (l.items || []).forEach((i) => {
            lemburKg += Number(i.kg || 0);
            lemburPax += Number(i.pax || 0);
            byType[i.produk] = (byType[i.produk] || 0) + Number(i.pax || 0);
            lemburByType[i.produk] = (lemburByType[i.produk] || 0) + Number(i.pax || 0);
          });
        });
        dough += lemburKg;
        pax += lemburPax;
        const types = Object.entries(byType)
          .map(([code, p]) => ({ code, pax: p, lembur: lemburByType[code] || 0 }))
          .sort((a, b) => b.pax - a.pax);
        setSummary({ dough, pax, count: data.length, types, lemburKg, lemburPax, lemburCount: lemburs.length });
      })
      .catch(() => {});
  }, [summaryPeriod, saving, rangeStart, rangeEnd]);

  const sameName =
    person1.trim() !== "" &&
    person1.trim().toLowerCase() === person2.trim().toLowerCase();

  const reset = () => {
    setPerson1("");
    setPerson2("");
    setDoughKg({});
    setDoughSel([]);
    setPax({});
    setSelected([]);
    setNotes("");
  };

  const toggleDough = (code) => {
    setDoughSel((prev) => {
      if (prev.includes(code)) {
        setDoughKg((p) => {
          const next = { ...p };
          delete next[code];
          return next;
        });
        setSelected((s) => s.filter((c) => c !== code));
        setPax((p) => {
          const next = { ...p };
          delete next[code];
          return next;
        });
        return prev.filter((c) => c !== code);
      }
      setSelected((s) => (s.includes(code) ? s : [...s, code]));
      return [...prev, code];
    });
  };

  const toggleType = (code) => {
    setSelected((prev) => {
      if (prev.includes(code)) {
        setPax((p) => {
          const next = { ...p };
          delete next[code];
          return next;
        });
        return prev.filter((c) => c !== code);
      }
      return [...prev, code];
    });
  };

  const buildPayload = () => {
    const dough_items = Object.entries(doughKg)
      .filter(([, v]) => Number(v) > 0)
      .map(([dough_type, v]) => ({ dough_type, kg: Number(v) }));
    const items = Object.entries(pax)
      .filter(([, v]) => Number(v) > 0)
      .map(([dimsum_type, v]) => ({ dimsum_type, pax: Number(v) }));
    const members = [person1, person2].map((m) => m.trim()).filter(Boolean);
    return { dough_items, items, members };
  };

  const validate = () => {
    const { dough_items, items, members } = buildPayload();
    if (dough_items.length === 0) {
      toast.error("Pilih & isi minimal 1 jenis adonan (kg)");
      return false;
    }
    if (items.length === 0) {
      toast.error("Isi minimal 1 jenis dimsum (pax)");
      return false;
    }
    if (members.length === 0) {
      toast.error("Isi minimal nama Peserta 1");
      return false;
    }
    if (sameName) {
      toast.error("Nama Peserta 1 & 2 tidak boleh sama");
      return false;
    }
    return true;
  };

  const openConfirm = () => {
    if (validate()) setConfirmOpen(true);
  };

  const buildTable = (dough_items, items) => {
    const tDough = dough_items.reduce((s, d) => s + Number(d.kg || 0), 0);
    const tPax = items.reduce((s, i) => s + Number(i.pax || 0), 0);
    const labels = [
      ...dough_items.map((d) => d.dough_type),
      ...items.map((i) => i.dimsum_type),
      "TOTAL",
    ];
    const w = Math.max(6, ...labels.map((l) => l.length));
    const line = (label, val, unit) => label.padEnd(w) + " : " + fmt(val) + " " + unit;
    let out = "ADONAN (kg):\n";
    dough_items.forEach((d) => (out += line(d.dough_type, d.kg, "kg") + "\n"));
    out += line("TOTAL", tDough, "kg") + "\n\n";
    out += "DIMSUM (pax):\n";
    items.forEach((i) => (out += line(i.dimsum_type, i.pax, "pax") + "\n"));
    out += line("TOTAL", tPax, "pax");
    return out;
  };

  const buildWaText = (dough_items, items, members) => {
    const head =
      `*REKAP PRODUKSI DIMSUM*\n\n` +
      `Tanggal: ${idDate(date)}\n` +
      `Peserta: ${members.join(" & ")}\n\n`;
    const footer = notes.trim() ? `\nCatatan: ${notes.trim()}\n` : "";
    return head + "```\n" + buildTable(dough_items, items) + "\n```\n" + footer;
  };

  const performSave = async (replaceId) => {
    const { dough_items, items, members } = buildPayload();
    setSaving(true);
    try {
      if (replaceId) {
        await api.put(`/recaps/${replaceId}`, { date, members, dough_items, items, notes });
      } else {
        await api.post("/recaps", { date, members, dough_items, items, notes });
      }
      toast.success(replaceId ? "✅ Data hari ini diganti!" : "✅ Rekap tersimpan di Riwayat!");
      window.open(
        `https://wa.me/?text=${encodeURIComponent(buildWaText(dough_items, items, members))}`,
        "_blank"
      );
      setConfirmOpen(false);
      setDupOpen(false);
      setDupExisting(null);
      reset();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan rekap");
    } finally {
      setSaving(false);
    }
  };

  const confirmSave = async () => {
    const { members } = buildPayload();
    const invalidMembers = members.filter(
      (m) => !memberOptions.some((o) => o.trim().toLowerCase() === m.trim().toLowerCase())
    );
    if (invalidMembers.length) {
      toast.error(`Nama peserta tidak terdaftar: ${invalidMembers.join(", ")}. Data ditolak.`);
      setConfirmOpen(false);
      return;
    }
    const norm = (arr) => [...(arr || [])].map((m) => m.trim().toLowerCase()).sort();
    const key = norm(members).join("|");
    try {
      const { data } = await api.get(`/recaps?start=${date}&end=${date}`);
      const recapsToday = data || [];
      const exact = recapsToday.find((r) => norm(r.members).join("|") === key);
      if (exact) {
        setDupExisting(exact);
        setConfirmOpen(false);
        setDupOpen(true);
        return;
      }
      const found = [];
      for (const m of members) {
        const r = recapsToday.find(
          (rr) =>
            norm(rr.members).join("|") !== key &&
            (rr.members || []).some((x) => x.trim().toLowerCase() === m.trim().toLowerCase())
        );
        if (r) {
          const partners = (r.members || []).filter(
            (x) => x.trim().toLowerCase() !== m.trim().toLowerCase()
          );
          found.push({ member: m, partners });
        }
      }
      if (found.length) {
        setConflicts(found);
        setConfirmOpen(false);
        setConflictOpen(true);
        return;
      }
    } catch (e) {
      /* jika gagal cek, lanjut simpan biasa */
    }
    performSave(null);
  };

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6" data-testid="recap-entry-form">
      <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
        <div>
          <label className={labelCls}>Tanggal</label>
          <input
            data-testid="recap-input-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <PersonAutocomplete
            label="Peserta 1"
            value={person1}
            onChange={setPerson1}
            options={memberOptions}
            exclude={person2}
            testid="recap-person-1"
            placeholder="Ketik / pilih nama…"
          />
          <div>
            <PersonAutocomplete
              label="Peserta 2 (opsional)"
              value={person2}
              onChange={setPerson2}
              options={memberOptions}
              exclude={person1}
              testid="recap-person-2"
              placeholder="Ketik / pilih nama…"
            />
            {sameName && (
              <p
                data-testid="recap-duplicate-warning"
                className="mt-1.5 text-sm font-bold text-rose-600"
              >
                Nama Peserta 1 & 2 tidak boleh sama
              </p>
            )}
          </div>
        </div>

        <div>
          <label className={labelCls}>Jenis, Adonan (Kg) & Dimsum (Pax)</label>
          <TypeAutocomplete
            options={doughTypes}
            selected={doughSel}
            onPick={toggleDough}
            testid="recap-dough-search"
            placeholder="Ketik jenis… (mis. ARJ)"
          />
          {doughSel.length === 0 ? (
            <p className="rounded-2xl bg-stone-50 border border-dashed border-stone-300 px-4 py-5 text-center text-sm font-semibold text-stone-400">
              Belum ada jenis dipilih.
            </p>
          ) : (
            <div className="space-y-4">
              {[...doughSel]
                .reverse()
                .map((code) => doughTypes.find((x) => x.code === code))
                .filter(Boolean)
                .map((d) => (
                  <div key={d.code} className="rounded-2xl bg-amber-50/60 border border-amber-200 p-3.5">
                    <div className="mb-2 relative flex items-center justify-center">
                      <span
                        className={`inline-block rounded-lg border px-2.5 py-1 text-xs font-bold ${
                          typeBadge(d.code)
                        }`}
                      >
                        {d.name}
                      </span>
                      <button
                        type="button"
                        data-testid={`recap-remove-dough-${d.code}`}
                        onClick={() => toggleDough(d.code)}
                        className="absolute right-0 grid place-items-center w-9 h-9 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 active:scale-95 transition-transform"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="mb-1.5 block text-center text-xs font-extrabold uppercase tracking-wider text-rose-600">
                          Kg
                        </span>
                        <Stepper
                          value={doughKg[d.code] ?? ""}
                          onChange={(v) => setDoughKg((p) => ({ ...p, [d.code]: v }))}
                          testid={`recap-input-dough-${d.code}`}
                        />
                      </div>
                      <div>
                        <span className="mb-1.5 block text-center text-xs font-extrabold uppercase tracking-wider text-emerald-600">
                          Pax
                        </span>
                        <Stepper
                          value={pax[d.code] ?? ""}
                          onChange={(v) => setPax((p) => ({ ...p, [d.code]: v }))}
                          testid={`recap-input-pax-${d.code}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Catatan</label>
          <textarea
            data-testid="recap-input-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Catatan opsional…"
            className={inputCls}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            data-testid="recap-submit-share-button"
            onClick={openConfirm}
            disabled={saving}
            className="flex-1 min-h-[52px] rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-md active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <MessageCircle className="w-5 h-5" /> Simpan & Bagikan WA
          </button>
          <button
            data-testid="recap-reset-button"
            onClick={reset}
            className="min-h-[52px] rounded-2xl bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold px-6 flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" /> Reset
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-3xl p-6 shadow-sm">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">
              Ringkasan Tersimpan
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {[
                ["day", "Hari"],
                ["week", "Pekan"],
                ["month", "Bulan"],
                ["all", "Semua"],
                ["custom", "Tanggal"],
              ].map(([p, label]) => (
                <button
                  key={p}
                  data-testid={`preview-filter-${p}`}
                  onClick={() => setSummaryPeriod(p)}
                  className={`min-h-[36px] rounded-xl px-3 font-bold text-xs transition-colors ${
                    summaryPeriod === p
                      ? "bg-rose-600 text-white shadow"
                      : "bg-white/70 text-stone-600 hover:bg-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {summaryPeriod === "custom" && (
              <div className="grid grid-cols-2 gap-2 mb-3" data-testid="preview-range-inputs">
                <div>
                  <label className="text-xs font-semibold text-stone-500">Dari Tanggal</label>
                  <input
                    data-testid="preview-range-start"
                    type="date"
                    value={rangeStart}
                    onChange={(e) => setRangeStart(e.target.value)}
                    className="w-full rounded-xl border-2 border-amber-200 bg-white px-2.5 py-2 text-sm font-semibold text-stone-800 focus:border-rose-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-stone-500">Sampai Tanggal</label>
                  <input
                    data-testid="preview-range-end"
                    type="date"
                    value={rangeEnd}
                    onChange={(e) => setRangeEnd(e.target.value)}
                    className="w-full rounded-xl border-2 border-amber-200 bg-white px-2.5 py-2 text-sm font-semibold text-stone-800 focus:border-rose-500 focus:outline-none"
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-stone-500">Adonan</p>
                <p
                  data-testid="preview-summary-dough"
                  className="font-mono-num text-2xl font-extrabold text-orange-700"
                >
                  {fmt(summary.dough)} <span className="text-sm">kg</span>
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-500">Dimsum</p>
                <p
                  data-testid="preview-summary-pax"
                  className="font-mono-num text-2xl font-extrabold text-emerald-700"
                >
                  {fmt(summary.pax)} <span className="text-sm">pax</span>
                </p>
              </div>
            </div>
            <p className="mt-2 text-xs font-semibold text-stone-400">
              {summary.count} rekap + {summary.lemburCount || 0} lembur tersimpan
            </p>
            {summary.lemburCount > 0 && (
              <p data-testid="preview-summary-lembur" className="mt-1 text-xs font-bold text-sky-700">
                Termasuk lembur: {fmt(summary.lemburKg)} kg · {fmt(summary.lemburPax)} pax
              </p>
            )}
            {summary.types && summary.types.length > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">
                  Detail Jenis Dimsum
                </p>
                <div className="space-y-1.5" data-testid="summary-dimsum-detail">
                  {summary.types.map((t) => (
                    <div
                      key={t.code}
                      data-testid={`summary-type-${t.code}`}
                      className="flex items-center justify-between gap-2"
                    >
                      <span
                        className={`inline-block rounded-lg border px-2 py-0.5 text-xs font-bold ${typeBadge(
                          t.code
                        )}`}
                      >
                        {t.code}
                      </span>
                      <span className="flex items-center gap-2">
                        {t.lembur > 0 && (
                          <span
                            data-testid={`summary-type-lembur-${t.code}`}
                            className="rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold text-sky-700"
                          >
                            {t.lembur === t.pax ? "Lembur" : `Lembur ${fmt(t.lembur)}`}
                          </span>
                        )}
                        <span className="font-mono-num text-sm font-bold text-emerald-700">
                          {fmt(t.pax)} pax
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmOpen && (
        <ConfirmModal
          date={date}
          members={[person1, person2].map((m) => m.trim()).filter(Boolean)}
          doughSel={doughSel}
          doughKg={doughKg}
          selected={selected}
          pax={pax}
          notes={notes}
          totalDough={totalDough}
          totalPax={totalPax}
          saving={saving}
          onEdit={() => setConfirmOpen(false)}
          onConfirm={confirmSave}
        />
      )}

      {dupOpen && dupExisting && (
        <DuplicateModal
          date={date}
          members={[person1, person2].map((m) => m.trim()).filter(Boolean)}
          oldRecap={dupExisting}
          newData={{ ...buildPayload(), notes }}
          saving={saving}
          onCancel={() => {
            setDupOpen(false);
            setDupExisting(null);
          }}
          onConfirm={() => performSave(dupExisting.id)}
        />
      )}

      {conflictOpen && (
        <ConflictModal
          date={date}
          conflicts={conflicts}
          onClose={() => {
            setConflictOpen(false);
            setConflicts([]);
          }}
        />
      )}
    </div>
  );
};
